import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { makeAuthenticatedGetRequest } from '../services/axios';
import { getUserInfo } from '../services/axios/endpoints';
import Sentry from '../services/sentry';
import { userMeResponseSchema } from '../stores/schemas/userMe';
import type { CompassModule, UserMeResponse } from '../stores/schemas/userMe';
import { useGlobalStore } from '../stores/useGlobalStore';
import { getUserType } from '../utils/functions/getUserType';

interface ApiError extends Error {
  response?: {
    data?: any;
    status?: number;
  };
}

export interface PermissionsContextValue {
  permissions: string[];
  loading: boolean;
  isFreemiumUser: boolean;
  currentPlan: string | null;
  compassModules: CompassModule[];
  compassModulesMap: Record<string, CompassModule>;
  whitelistedProjectTypes: string[];
  isExpertUser: boolean;
  userData: UserMeResponse | null;
  isEarlyStage: boolean;
  isCompassEnabled: boolean;
  latestAppVersion: string;
  isVersionMismatch: boolean;
  isDemoUser: boolean;
}

export const PermissionsContext = createContext<PermissionsContextValue>({
  permissions: [],
  loading: true,
  isFreemiumUser: true,
  currentPlan: null,
  compassModules: [],
  compassModulesMap: {},
  whitelistedProjectTypes: [],
  isExpertUser: false,
  userData: null,
  isEarlyStage: false,
  isCompassEnabled: false,
  latestAppVersion: '',
  isVersionMismatch: false,
  isDemoUser: false,
});

const BLACKLISTED_ROUTES = ['/register', '/signup', '/account-signup', '/account-request', '/demo'];

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const isBlacklistedRoute = BLACKLISTED_ROUTES.includes(window.location.pathname);
  const user = useGlobalStore((s) => s.user);
  const updateUser = useGlobalStore((s) => s.updateUser);
  const demoSession = useGlobalStore((s) => s.demoSession);
  const [previousSuccessfulData, setPreviousSuccessfulData] = useState<UserMeResponse | null>(null);

  // A demo (magic-link) prospect has no /user/me — skip the fetch and never
  // throw on its expected 401; the demo route guard already scopes them.
  const skipUserInfo = isBlacklistedRoute || !!demoSession;

  const { data, isLoading, error, isError, failureCount } = useQuery({
    queryKey: ['user-info'],
    queryFn: async () => {
      const res = await makeAuthenticatedGetRequest(getUserInfo);
      return userMeResponseSchema.parse(res?.data);
    },
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: !skipUserInfo,
  });

  useEffect(() => {
    if (data) {
      setPreviousSuccessfulData(data);
    }
  }, [data]);

  useEffect(() => {
    if (isError && error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        tags: {
          api: 'user-me',
          context: 'permissions-context',
        },
        extra: {
          errorMessage: error?.message,
          errorResponse: apiError?.response?.data,
          statusCode: apiError?.response?.status,
          failureCount,
          hasPreviousData: !!previousSuccessfulData,
        },
      });
    }
  }, [isError, error, failureCount, previousSuccessfulData]);

  const activeData = data || previousSuccessfulData;

  useEffect(() => {
    if (!data || !user) return;

    const updates: Record<string, unknown> = {};

    if (data.company_type && user.company_type !== data.company_type) {
      updates.company_type = data.company_type;
    }

    const compassModulesAccess =
      data.compass_modules?.map(({ display_name }) => display_name) || [];
    if (JSON.stringify(user.compass_modules_access) !== JSON.stringify(compassModulesAccess)) {
      updates.compass_modules_access = compassModulesAccess;
    }

    if (data.plan && user.ratings_access_type !== data.plan) {
      updates.ratings_access_type = data.plan;
    }

    const calculatedUserType = getUserType(user.company, data.is_demo, data.is_plan_public);
    if (user.user_type !== calculatedUserType) {
      updates.user_type = calculatedUserType;
    }

    if (
      data.compass_subscription_type &&
      user.compass_access_type !== data.compass_subscription_type
    ) {
      updates.compass_access_type = data.compass_subscription_type;
    }

    if (Object.keys(updates).length > 0) {
      updateUser(updates as Partial<NonNullable<typeof user>>);
    }
  }, [data, user, updateUser]);

  const compassModulesMap = useMemo(() => {
    return ((activeData?.compass_modules ?? []) as CompassModule[]).reduce<
      Record<string, CompassModule>
    >((acc, mod) => {
      acc[mod.name] = mod;
      return acc;
    }, {});
  }, [activeData?.compass_modules]);

  const whitelistedProjectTypes = useMemo(() => {
    return (
      activeData?.compass_modules
        ?.filter((mod) => mod.has_permission)
        ?.map((mod) => mod.project_types?.[0]?.name) || []
    );
  }, [activeData?.compass_modules]);

  const isExpertUser = useMemo(() => {
    return activeData?.plan?.toLowerCase()?.includes('expert') ?? false;
  }, [activeData?.plan]);

  if (isError && !previousSuccessfulData && !skipUserInfo) {
    throw new Error('Failed to load user permissions. Please try again later.');
  }

  return (
    <PermissionsContext.Provider
      value={{
        permissions: activeData?.features?.map((f) => f.name) || [],
        loading: isLoading,
        isFreemiumUser: activeData?.is_plan_public ?? activeData?.freemium_experience ?? true,
        currentPlan: activeData?.plan ?? null,
        compassModules: activeData?.compass_modules || [],
        compassModulesMap,
        whitelistedProjectTypes,
        isExpertUser,
        userData: activeData ?? null,
        isEarlyStage: !activeData?.compass,
        isCompassEnabled: activeData?.compass ?? false,
        latestAppVersion: activeData?.latest_ui_version ?? '',
        isVersionMismatch: Boolean(
          activeData?.latest_ui_version &&
            process.env.GITHUB_VERSION_NUMBER &&
            process.env.GITHUB_VERSION_NUMBER !== activeData?.latest_ui_version
        ),
        isDemoUser: activeData?.is_demo ?? false,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = () => useContext(PermissionsContext);
